import { Box, Select, MultiSelect, Badge, SegmentedControl, UnstyledButton } from '@mantine/core';
import { SurveyLogicAction, SurveyLogicElement, SurveyLogicScope, SurveyLogicActionType } from '@vision/ui/interfaces';
import { memo, useCallback, useMemo } from 'react';
import { TextWithEmoji } from '@vision/ui/components';
import { useActionBuilder } from './hooks';
import classes from './ActionBuilder.module.css';

interface ActionBuilderProps {
  ruleId: string;
  action: SurveyLogicAction;
  elements: SurveyLogicElement[];
  activeLanguageCode: string;
}

export const ActionBuilder = memo(function ActionBuilder({
  ruleId,
  action,
  elements,
  activeLanguageCode,
}: ActionBuilderProps) {
  const {
    t,
    elementOptions,
    elementOptionsWithOptions,
    statementOptions,
    optionOptions,
    eventOptions,
    selectedMatrixElements,
    effectiveTargetElementKeys,
    targetOptionValues,
    targetStatementValues,
    handleScopeChange,
    handleActionTypeChange,
    handleTargetElementsChange,
    handleTargetOptionsChange,
    handleTargetStatementsChange,
    handleTargetEventsChange,
  } = useActionBuilder({ ruleId, action, elements, activeLanguageCode });

  const renderSelectOption = useCallback(
    ({ option }: { option: { value: string; label: string } }) => <TextWithEmoji text={option.label} />,
    [],
  );

  const scopeOptions = useMemo(
    () => [
      { value: SurveyLogicScope.QUESTION, label: t('action.scopeQuestion') },
      { value: SurveyLogicScope.OPTION, label: t('action.scopeOption') },
      { value: SurveyLogicScope.EVENT_DISPATCH, label: t('action.scopeEvent') },
    ],
    [t],
  );

  return (
    <Box className={classes.container}>
      <Box className={classes.headerRow}>
        <Badge className={classes.actionBadge}>{t('actionBuilder.title')}</Badge>

        <SegmentedControl
          value={action.scope}
          onChange={(value) => handleScopeChange(value as SurveyLogicScope)}
          data={scopeOptions}
          className={classes.scopeToggle}
          classNames={{ label: classes.scopeLabel }}
          data-testid="segmented-scope"
        />
      </Box>

      <Box className={classes.contentRow}>
        {action.scope === SurveyLogicScope.QUESTION && (
          <>
            <Box className={classes.field}>
              <MultiSelect
                value={action.targetElementKeys || []}
                onChange={handleTargetElementsChange}
                data={elementOptions}
                placeholder={t('action.selectTargetElements')}
                searchable={true}
                clearable={true}
                hidePickedOptions={true}
                className={classes.dropdown}
                renderOption={renderSelectOption}
                data-testid="select-target-elements"
              />
            </Box>

            {selectedMatrixElements.length > 0 && (
              <Box className={classes.field}>
                <MultiSelect
                  value={targetStatementValues}
                  onChange={handleTargetStatementsChange}
                  data={statementOptions}
                  placeholder={t('conditionBuilder.selectStatement')}
                  searchable={true}
                  clearable={true}
                  hidePickedOptions={true}
                  className={classes.dropdown}
                  renderOption={renderSelectOption}
                  data-testid="select-target-statements"
                />
              </Box>
            )}
          </>
        )}

        {action.scope === SurveyLogicScope.OPTION && (
          <>
            <Box className={classes.field}>
              <Select
                value={effectiveTargetElementKeys[0] || null}
                onChange={(value) => handleTargetElementsChange(value ? [value] : [])}
                data={elementOptionsWithOptions}
                placeholder={t('conditionBuilder.selectQuestion')}
                searchable={true}
                clearable={true}
                className={classes.dropdown}
                renderOption={renderSelectOption}
                data-testid="select-target-question"
              />
            </Box>

            <Box className={classes.field}>
              <MultiSelect
                value={targetOptionValues}
                onChange={handleTargetOptionsChange}
                data={optionOptions}
                placeholder={t('conditionBuilder.selectOption')}
                searchable={true}
                clearable={true}
                hidePickedOptions={true}
                disabled={!effectiveTargetElementKeys.length}
                className={classes.dropdown}
                renderOption={renderSelectOption}
                data-testid="select-target-options"
              />
            </Box>
          </>
        )}

        {action.scope === SurveyLogicScope.EVENT_DISPATCH && (
          <Box className={classes.field}>
            <Select
              value={action.targetEvents?.[0] || null}
              onChange={handleTargetEventsChange}
              data={eventOptions}
              placeholder={t('action.selectTargetEvents')}
              searchable={true}
              clearable={true}
              className={classes.dropdown}
              data-testid="select-target-events"
            />
          </Box>
        )}

        {action.scope !== SurveyLogicScope.EVENT_DISPATCH && (
          <Box className={classes.actionTypeGroup}>
            <UnstyledButton
              className={`${classes.actionTypeButton} ${action.type === SurveyLogicActionType.MAKE_VISIBLE ? classes.actionTypeActive : ''}`}
              onClick={() => handleActionTypeChange(SurveyLogicActionType.MAKE_VISIBLE)}
              data-testid="button-action-show"
            >
              {t('action.makeVisible')}
            </UnstyledButton>
            <UnstyledButton
              className={`${classes.actionTypeButton} ${action.type === SurveyLogicActionType.MAKE_INVISIBLE ? classes.actionTypeActive : ''}`}
              onClick={() => handleActionTypeChange(SurveyLogicActionType.MAKE_INVISIBLE)}
              data-testid="button-action-hide"
            >
              {t('action.makeInvisible')}
            </UnstyledButton>
            {action.scope === SurveyLogicScope.QUESTION && (
              <UnstyledButton
                className={`${classes.actionTypeButton} ${action.type === SurveyLogicActionType.MAKE_REQUIRED ? classes.actionTypeActive : ''}`}
                onClick={() => handleActionTypeChange(SurveyLogicActionType.MAKE_REQUIRED)}
                data-testid="button-action-required"
              >
                {t('action.makeRequired')}
              </UnstyledButton>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
});
